// How a part's footprint resizes (docs/claude/objects.md, *Resizing a part*):
// one rule per kind of footprint, so the size a handle drag asks for, the size it
// really gets and where its centre goes are worked out in ONE place:
//  - `footprintOf` is the one category list (a run, a channel, a drain, a heater
//    block, a room, any other part);
//  - `reachableSize` is the size a resize to w × d really reaches (each kind's
//    floor, a drain kept round, a room bounded by its walls' locked figures);
//  - `resizeByHandle` places the centre for THAT size, so past a floor the corner
//    opposite the handle stays put instead of the part sliding under the cursor;
//  - `resizeTo` writes it into the part — what scales with it, the heaters kept
//    inside a sauna — and leaves the room's regeneration to its caller.
// DOM-free: what only the app knows comes in through `ResizeEnv`.
import type {
  ProductInstance,
  Vec2,
} from '../shared/types';
import { unreachable } from '../shared/exhaustive';
import { traitsOf } from '../shared/categories';
import {
  benchDepthFromSlats,
  benchInterior,
  scaleBenchTiers,
} from './benches';
import { isFlatCeiling } from './ceiling';
import {
  clampDoor,
  roomDoors,
} from './door';
import {
  addVec,
  drawsHeaterBlock,
  heaterGuard,
  heaterOuterHalfExtents,
  instanceToWorld,
  polyBenchLength,
  rotatePoint,
  scaleGeometry,
  subVec,
  toInstanceLocal,
} from './geometry';
import {
  clampToWall,
  scaleGlass,
} from './glass';
import {
  isPolyRoom,
  polyEdgeLengths,
  rescaleEdgeFeatures,
  roomResizeConstraints,
  type VertexRemap,
} from './polyRoom';
import type { Handle } from './render';
import { defaultSaunaHeight } from './saunaLayout';

/** The smallest footprint dimension (mm) anything takes: 1 mm granularity. */
export const MIN_SIZE = 1;
/** A heater (or shower) block's smallest body (mm). */
const HEATER_MIN = 10;

/** How a part's footprint resizes:
 *  - `run`: a salt wall / heated bench / sauna bench drawn along its `nodes`;
 *    the width is the run's length, the depth its cross-section;
 *  - `channel`: a drain channel (canalina), free along its length;
 *  - `drain`: a floor drain, kept round;
 *  - `heater`: a heater or shower block, its handles on its overall box (body,
 *    clearance gap and cover);
 *  - `room`: a room, its polygon scaled and its features following their walls;
 *  - `part`: anything else, its drawing scaled with it. */
export type Footprint =
  | 'channel'
  | 'drain'
  | 'heater'
  | 'part'
  | 'room'
  | 'run';

/** The one category list: how this part's footprint resizes. */
export function footprintOf(inst: ProductInstance): Footprint {
  if (traitsOf(inst.category).run) return 'run';
  if (inst.category === 'canalina') return 'channel';
  if (inst.category === 'drain') return 'drain';
  if (drawsHeaterBlock(inst)) return 'heater';
  return inst.autoBenches ? 'room' : 'part';
}

/** The size a resize of this part to `w` × `d` really reaches. */
export function reachableSize(inst: ProductInstance, w: number, d: number): { w: number; d: number } {
  const kind = footprintOf(inst);
  switch (kind) {
    case 'run':
      // A salt wall keeps its fixed cross-section; a sauna bench's depth is its
      // boards' and gaps'; a heated bench extrudes to the depth asked.
      return {
        w: Math.max(inst.category === 'saltwall' ? 120 : 200, w),
        d: inst.category === 'saltwall' ? 191 : inst.category === 'saunabench' ? benchDepthFromSlats(inst) ?? inst.depth : Math.max(50, d),
      };
    case 'channel':
      return { w: Math.max(50, w), d: Math.max(20, d) };
    case 'drain': {
      const s = Math.max(30, w, d); // both follow the larger
      return { w: s, d: s };
    }
    case 'heater':
      return { w: Math.max(HEATER_MIN, w), d: Math.max(HEATER_MIN, d) };
    case 'room': {
      // A pinned figure (or a door, locked by default) keeps its length, so a
      // room neither shrinks below nor grows past what its walls' locks allow.
      let rw = Math.max(MIN_SIZE, w), rd = Math.max(MIN_SIZE, d);
      if (isPolyRoom(inst)) {
        const { minW, maxW, minD, maxD } = roomResizeConstraints(inst);
        rw = Math.min(Math.max(rw, minW), maxW);
        rd = Math.min(Math.max(rd, minD), maxD);
      }
      return { w: rw, d: rd };
    }
    case 'part':
      return { w: Math.max(MIN_SIZE, w), d: Math.max(MIN_SIZE, d) };
    default:
      return unreachable(kind, 'footprint');
  }
}

export interface ResizeResult {
  width: number;
  depth: number;
  center: Vec2;
}

/** Where the centre of a box resized to `w` × `d` by this handle goes: the edge
 *  or corner opposite the handle stays where it was. */
function anchoredCenter(box: ProductInstance, handle: Handle, w: number, d: number): Vec2 {
  const { sx, sy } = handle;
  const anchorLocal = { x: sx === 0 ? 0 : (-sx * box.width) / 2, y: sy === 0 ? 0 : (-sy * box.depth) / 2 };
  const anchorWorld = instanceToWorld(box, anchorLocal);
  return addVec(anchorWorld, rotatePoint({ x: (sx * w) / 2, y: (sy * d) / 2 }, box.rotation));
}

/**
 * The footprint and centre a box takes when a resize handle is dragged to
 * `mouseWorld`, keeping the opposite edge/corner fixed in world space. Solved in
 * the box's local frame so it works at any rotation.
 */
export function computeResize(inst: ProductInstance, handle: Handle, mouseWorld: Vec2, minSize: number): ResizeResult {
  const { sx, sy } = handle;
  const anchorLocal = { x: sx === 0 ? 0 : (-sx * inst.width) / 2, y: sy === 0 ? 0 : (-sy * inst.depth) / 2 };
  const anchorWorld = instanceToWorld(inst, anchorLocal);
  const v = rotatePoint(subVec(mouseWorld, anchorWorld), -inst.rotation);
  const width = sx !== 0 ? Math.max(minSize, sx * v.x) : inst.width;
  const depth = sy !== 0 ? Math.max(minSize, sy * v.y) : inst.depth;
  return { width, depth, center: anchoredCenter(inst, handle, width, depth) };
}

/** What a handle drag needs besides the part, the handle and the pointer. */
export interface HandleResize {
  /** Link W & H: width and depth keep their ratio (never a room's). */
  linkWH: boolean;
  /** The pointer snapped onto a vertex: a corner handle goes there, ratio or not. */
  onVertex: boolean;
}

/**
 * A handle drag's result: the body size the part will REALLY get, and the centre
 * that keeps the corner opposite the handle fixed at that size. A heater's
 * handles sit on its overall box (body + gap + cover): the drag is solved in that
 * box, the margins fixed, and handed back as the body.
 */
export function resizeByHandle(inst: ProductInstance, handle: Handle, mouseWorld: Vec2, opts: HandleResize): ResizeResult {
  const kind = footprintOf(inst);
  let mx = 0, my = 0;
  if (kind === 'heater') {
    const g = heaterGuard(inst);
    const outer = heaterOuterHalfExtents(inst.width, inst.depth, g.gap, g.cover);
    mx = outer.hw - inst.width / 2;
    my = outer.hd - inst.depth / 2;
  }
  const box = { ...inst, width: inst.width + 2 * mx, depth: inst.depth + 2 * my };
  let { width, depth } = computeResize(box, handle, mouseWorld, MIN_SIZE);
  // A room's width and depth are independent, so it resizes freely: linking a
  // mid-edge handle would scale the other axis about the centre and slide the
  // walls not grabbed. A corner snapped onto a vertex goes THERE: the marker
  // promised it (a mid-edge handle keeps the ratio — its one axis lands anyway).
  const { sx, sy } = handle;
  if (opts.linkWH && kind !== 'room' && box.width > 0 && box.depth > 0 && (sx !== 0 || sy !== 0) && !(opts.onVertex && sx !== 0 && sy !== 0)) {
    // A one-axis handle drives the other; a corner follows the faster axis.
    const f = sy === 0 ? width / box.width : sx === 0 ? depth / box.depth : Math.max(width / box.width, depth / box.depth);
    width = Math.max(MIN_SIZE, box.width * f);
    depth = Math.max(MIN_SIZE, box.depth * f);
  }
  const body = reachableSize(inst, width - 2 * mx, depth - 2 * my);
  return { width: body.w, depth: body.d, center: anchoredCenter(box, handle, body.w + 2 * mx, body.d + 2 * my) };
}

/** What resizing a part needs from the app. */
export interface ResizeEnv {
  /** Everything standing in a room (a sauna's heaters are kept inside it). */
  contents: (room: ProductInstance) => ProductInstance[];
  /** A part that stays where it is (on a locked layer). */
  locked: (id: string) => boolean;
  /** Write a room's top, carrying what rests on it and hangs under it (main.ts editRoomTop). */
  editTop: (room: ProductInstance, write: () => void) => void;
}

/**
 * Resize a part to (at most what it can reach of) `w` × `d`, keeping what is
 * drawn with it in step: a run scales along its nodes, a part's drawing scales,
 * a room scales its polygon and its features follow their walls, its height
 * follows its depth unless set by hand, and a sauna's heaters stay inside it.
 * The room is NOT regenerated: the caller does that after, with the vertex remap
 * handed back (a pane's jambs moved shift the corners' indices).
 */
export function resizeTo(inst: ProductInstance, w: number, d: number, env: ResizeEnv): VertexRemap | null {
  const size = reachableSize(inst, w, d);
  const kind = footprintOf(inst);
  if (kind === 'run') {
    // Scaled about the nodes' centroid to the run length asked.
    if (inst.nodes && inst.nodes.length >= 2) {
      const curLen = polyBenchLength(inst.nodes);
      if (curLen > 0 && Math.abs(size.w - curLen) > 0.5) {
        const f = size.w / curLen;
        const cx = inst.nodes.reduce((s, n) => s + n.x, 0) / inst.nodes.length;
        const cy = inst.nodes.reduce((s, n) => s + n.y, 0) / inst.nodes.length;
        inst.nodes = inst.nodes.map((n) => ({ x: cx + (n.x - cx) * f, y: cy + (n.y - cy) * f }));
        inst.x = cx;
        inst.y = cy;
      }
      inst.width = Math.round(polyBenchLength(inst.nodes));
    } else {
      inst.width = size.w;
    }
    inst.depth = size.d;
    return null;
  }
  if (kind === 'channel' || kind === 'drain') {
    inst.width = size.w;
    inst.depth = size.d;
    return null;
  }
  const newW = size.w, newD = size.d;
  // A polygonal room's edge-hosted features (glass, openings, benches, partitions)
  // are anchored to an EDGE, not to a rectangular side, so they follow their own
  // wall's new length through `rescaleEdgeFeatures` below — which is also what
  // honours a pinned figure. Scaling them by the footprint's fx/fy here instead
  // would move them by the wrong wall's factor and then clamp them into the
  // rectangular span, where two panes on one wall can collapse onto each other.
  const edgeHosted = (o: { edge?: number; innerWallId?: string }): boolean => o.edge != null || o.innerWallId != null;
  const lensBefore = isPolyRoom(inst) ? polyEdgeLengths(inst) : null;
  if (inst.width > 0 && inst.depth > 0) {
    const fx = newW / inst.width, fy = newD / inst.depth;
    if (inst.glass && inst.glass.length) scaleGlass(inst.glass.filter((g) => !edgeHosted(g)), fx, fy);
    if (inst.openings && inst.openings.length) scaleGlass(inst.openings.filter((o) => !edgeHosted(o)), fx, fy);
    // Drawing-editor shapes scale with the footprint, about the local centre.
    const scalePt = (p: Vec2): Vec2 => ({ x: p.x * fx, y: p.y * fy });
    for (const dp of inst.drawPolygons || []) {
      dp.points = dp.points.map(scalePt);
      if (dp.holes) dp.holes = dp.holes.map((h) => h.map(scalePt));
    }
    for (const dw of inst.drawWalls || []) dw.points = dw.points.map(scalePt);
    // Inner walls: pos runs along their wall, length runs perpendicular into it.
    for (const iw of inst.innerWalls || []) {
      if (iw.edge != null) continue; // edge-hosted: rescaleEdgeFeatures moves it
      const horiz = iw.wall === 'front' || iw.wall === 'back';
      iw.pos *= horiz ? fx : fy;
      iw.length *= horiz ? fy : fx;
      // Its bench spans run along the wall; their depth stays.
      const runScale = horiz ? fy : fx;
      for (const face of ['a', 'b'] as const) {
        for (const leg of iw.benches?.[face] ?? []) { leg.start *= runScale; leg.end *= runScale; }
      }
    }
  }
  if (kind === 'room') {
    // Customised benches scale with the interior (the managed leg model only).
    if (inst.width > 0 && inst.depth > 0) scaleBenchTiers(inst, inst.width, inst.depth, newW, newD);
    // A polygonal room scales its outline about its centre; the walls, benches,
    // door and glass all re-derive from it.
    if (isPolyRoom(inst) && inst.width > 0 && inst.depth > 0) {
      const fx = newW / inst.width, fy = newD / inst.depth;
      inst.polygon = inst.polygon!.map((p) => ({ x: p.x * fx, y: p.y * fy }));
    }
    // The height keeps to its depth-based default unless set by hand; a shaped
    // ceiling keeps both its heights (the pitch follows).
    if (isFlatCeiling(inst) && (inst.height == null || inst.height === defaultSaunaHeight(inst.depth))) {
      env.editTop(inst, () => { inst.height = defaultSaunaHeight(newD); });
    }
    inst.width = newW;
    inst.depth = newD;
    if (!isPolyRoom(inst)) for (const door of roomDoors(inst)) if (door.innerWallId == null) clampDoor(inst, door);
    // Edge-hosted features follow their own wall's new length (and a pinned figure
    // keeps its value); only the legacy rectangular ones clamp to a wall span.
    const remap = lensBefore ? rescaleEdgeFeatures(inst, lensBefore) : null;
    for (const seg of [...(inst.glass || []), ...(inst.openings || [])]) {
      if (edgeHosted(seg)) continue;
      seg.start = clampToWall(inst, seg.wall, seg.start);
      seg.end = clampToWall(inst, seg.wall, seg.end);
    }
    if (inst.category === 'sauna') {
      for (const h of env.contents(inst)) if (traitsOf(h.category).carvesBenches && !env.locked(h.id)) keepInRoom(h, inst);
    }
    return remap;
  }
  if (inst.geometry) inst.geometry = scaleGeometry(inst.geometry, newW / inst.width, newD / inst.depth);
  inst.width = newW;
  inst.depth = newD;
  if (!isPolyRoom(inst)) for (const door of roomDoors(inst)) if (door.innerWallId == null) clampDoor(inst, door);
  return null;
}

/** Move a heater (or a feature cube) so its footprint stays inside the room's
 *  inner walls; centred on an axis the room is too small for. Its turn is left
 *  as it is: it is aligned to the room once, when dropped. */
export function keepInRoom(part: ProductInstance, room: ProductInstance): void {
  const { ix0, ix1, iy0, iy1 } = benchInterior(room);
  const hw = part.width / 2, hd = part.depth / 2;
  const local = toInstanceLocal(room, part);
  const cx = ix1 - hw >= ix0 + hw ? Math.max(ix0 + hw, Math.min(ix1 - hw, local.x)) : (ix0 + ix1) / 2;
  const cy = iy1 - hd >= iy0 + hd ? Math.max(iy0 + hd, Math.min(iy1 - hd, local.y)) : (iy0 + iy1) / 2;
  const w = instanceToWorld(room, { x: cx, y: cy });
  part.x = w.x;
  part.y = w.y;
}
